/**
 * @fileoverview Editor de uma triagem, em abas: Geral (nome, modelo, usuário
 * IA), Itens (o que pedir), Mensagens (persona e textos), Contato ativo (como
 * abrir a conversa: conexão e template), Ordens de serviço (começar pelas OS,
 * sozinho ou pelo botão), Cadastro do contato (nome montado com os dados da
 * triagem), CRM, Lembretes (cliente que parou de responder) e Conclusão.
 * "Salvar rascunho" grava; "Publicar" grava (se preciso) e congela uma versão
 * nova, que é a usada pelas triagens iniciadas daí em diante.
 */

import React, { useEffect, useMemo, useState } from "react";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import { ChevronLeft, X } from "lucide-react";
import { toast } from "react-toastify";

import api from "../../../../services/api";
import toastError from "../../../../errors/toastError";
import { i18n } from "../../../../translate/i18n";
import ModernSelect from "../../../../components/ModernSelect";
import useStyles from "./styles";
import ItemsEditor from "./ItemsEditor";
import OutreachEditor from "./OutreachEditor";
import WorkOrdersEditor from "./WorkOrdersEditor";
import CrmEditor from "./CrmEditor";
import DestinationFields from "./DestinationFields";
import FollowUpEditor from "./FollowUpEditor";
import WorkOrderRoutesEditor from "./WorkOrderRoutesEditor";
import CustomerExportEditor from "./CustomerExportEditor";
import ContactEditor, { contactNameProblemText } from "./ContactEditor";
import SectionTitle from "./InfoTip";
import { contactNameProblem } from "./contactNameFormat";
import { missingVariable } from "./metaTemplates";
import {
  DEFAULT_EXPIRE_AFTER_DAYS,
  FOLLOW_UP_MAX_MINUTES,
  FOLLOW_UP_MIN_MINUTES,
  WARRANTIES,
  WORK_ORDER_ROUTE_MAX_VALUE_LENGTH,
  WORK_ORDER_ROUTE_MAX_VALUES,
  acceptsExtraction,
  emptyForm,
  fromDefinition,
  itemsOfWarranty,
  routeValues,
  snapshot,
  toMinutes,
  toPayload,
} from "./formState";

const TABS = ["general", "items", "messages", "outreach", "workOrders", "contact", "crm", "followUp", "completion"];
const e = (key, params) => i18n.t(`triage.manager.editor.${key}`, params);

const metaFrom = (def) => ({
  id: def.id,
  status: def.status,
  currentVersion: def.currentVersion,
  hasUnpublishedChanges: def.hasUnpublishedChanges,
});

/**
 * Primeiro problema que a tela já sabe apontar (o backend confere o resto).
 * `view`: a lista aberta na aba Itens (com a lista de cada garantia).
 */
const validar = (form, view = "in") => {
  if (!form.name.trim()) return { tab: "general", message: e("errors.name") };
  if (!form.items.length) return { tab: "items", message: e("errors.noItems") };
  const separado = form.warrantySplit.enabled;
  for (let i = 0; i < form.items.length; i += 1) {
    const it = form.items[i];
    // Com a lista de cada garantia, o número é o da lista em que o item aparece
    // (a dele, ou a aberta quando ele vale nas duas), e é ela que abre.
    const lista = separado ? it.warranty || view : null;
    const n = (lista ? itemsOfWarranty(form.items, lista) : form.items).indexOf(it) + 1;
    const onde = { tab: "items", uid: it.uid, ...(lista && { warranty: lista }) };
    if (!it.name.trim()) return { ...onde, message: e("errors.itemName", { n }) };
    if (!it.requestMessage.trim()) return { ...onde, message: e("errors.itemRequest", { n }) };
    if (it.extraction.enabled && acceptsExtraction(it.inputType)) {
      if (!it.extraction.fields.length) return { ...onde, message: e("errors.noFields", { n }) };
      if (it.extraction.fields.some((f) => !f.label.trim())) return { ...onde, message: e("errors.fieldLabel", { n }) };
    }
  }
  if (!form.items.some((it) => it.required)) return { tab: "items", message: e("errors.noRequired") };
  // Cada lista em uso precisa de um item obrigatório (sem o início pelas OS,
  // só a de "sem garantia" é usada).
  if (separado) {
    const emUso = form.workOrders.enabled ? WARRANTIES : [form.warrantySplit.whenUnknown];
    const lista = emUso.find((w) => !itemsOfWarranty(form.items, w).some((it) => it.required));
    if (lista) return { tab: "items", warranty: lista, message: e("errors.warrantyRequired", { list: e(`items.warranty.lists.${lista}`) }) };
  }
  if (!form.completion.trim()) return { tab: "messages", message: e("errors.completion") };
  const tpl = form.outreach.whatsappId !== "" ? form.outreach.template : null;
  for (const [n, origem] of Object.entries(tpl?.variables || {})) {
    if (!origem) return { tab: "outreach", message: e("errors.outreachVariable", { n }) };
    if (origem.startsWith("fixed:") && !origem.slice(6).trim()) return { tab: "outreach", message: e("errors.outreachFixed", { n }) };
  }
  const os = form.workOrders;
  if (Object.values(tpl?.variables || {}).some((v) => v.startsWith("workOrder.")) && !os.enabled) {
    return { tab: "outreach", message: e("errors.outreachWorkOrder") };
  }
  if (os.enabled) {
    if (os.sourceId === "") return { tab: "workOrders", message: e("errors.workOrderSource") };
    if (!os.statuses.length) return { tab: "workOrders", message: e("errors.workOrderStatuses") };
    if (!os.types.length) return { tab: "workOrders", message: e("errors.workOrderTypes") };
    if (os.startMode === "auto") {
      if (!os.days.length) return { tab: "workOrders", message: e("errors.workOrderDays") };
      if (!/^\d{2}:\d{2}$/.test(os.start) || !/^\d{2}:\d{2}$/.test(os.end) || os.start >= os.end) {
        return { tab: "workOrders", message: e("errors.workOrderWindow") };
      }
    }
    if (form.outreach.whatsappId === "") return { tab: "outreach", message: e("errors.workOrderConnection") };
  }
  if (form.contactName.enabled) {
    const problema = contactNameProblem(form.contactName.format, { items: form.items, workOrdersEnabled: os.enabled });
    if (problema) return { tab: "contact", message: contactNameProblemText(problema) };
  }
  const fu = form.followUp;
  if (fu.enabled) {
    if (!fu.stages.length) return { tab: "followUp", message: e("errors.followUpNone") };
    const valido = (m) => Number.isInteger(m) && m >= FOLLOW_UP_MIN_MINUTES && m <= FOLLOW_UP_MAX_MINUTES;
    let anterior = 0;
    for (let i = 0; i < fu.stages.length; i += 1) {
      const st = fu.stages[i];
      const n = i + 1;
      const minutos = toMinutes(st.amount, st.unit);
      if (!valido(minutos)) return { tab: "followUp", message: e("errors.followUpTime", { n }) };
      if (minutos <= anterior) return { tab: "followUp", message: e("errors.followUpOrder", { n }) };
      anterior = minutos;
      if (!st.message.trim()) return { tab: "followUp", message: e("errors.followUpMessage", { n }) };
      const falta = missingVariable(st.template);
      if (falta) return { tab: "followUp", message: e("errors.followUpVariable", { n, v: falta.n }) };
    }
    if (fu.finalAction === "handoff") {
      const minutos = toMinutes(fu.finalAmount, fu.finalUnit);
      if (!valido(minutos) || minutos <= anterior) return { tab: "followUp", message: e("errors.followUpFinal") };
      anterior = minutos;
    }
    if (!fu.days.length) return { tab: "followUp", message: e("errors.followUpDays") };
    if (!/^\d{2}:\d{2}$/.test(fu.start) || !/^\d{2}:\d{2}$/.test(fu.end) || fu.start >= fu.end) {
      return { tab: "followUp", message: e("errors.followUpWindow") };
    }
    // Vazio: o servidor usa o padrão (30 dias).
    const prazo = form.expireAfterDays === "" ? DEFAULT_EXPIRE_AFTER_DAYS : Number(form.expireAfterDays) || 0;
    if (prazo > 0 && anterior >= prazo * 1440) return { tab: "followUp", message: e("errors.followUpExpire", { days: prazo }) };
  }
  const crm = form.crm;
  if (crm.enabled) {
    if (crm.boardId === "") return { tab: "crm", message: e("errors.crmBoard") };
    if (crm.entry === "") return { tab: "crm", message: e("errors.crmEntry") };
    if (crm.completed === "") return { tab: "crm", message: e("errors.crmCompleted") };
    if (String(crm.entry) === String(crm.completed)) return { tab: "crm", message: e("errors.crmSame") };
    const concluidas = String(crm.completed);
    if (String(crm.inProgress) === concluidas || String(crm.attention) === concluidas) {
      return { tab: "crm", message: e("errors.crmCompletedOnly") };
    }
    const porChave = new Map(form.items.filter((it) => it.key).map((it) => [it.key, it]));
    for (let i = 0; i < crm.rules.length; i += 1) {
      const r = crm.rules[i];
      const item = porChave.get(r.requirementKey);
      if (!item || r.columnId === "") return { tab: "crm", message: e("errors.crmRule", { n: i + 1 }) };
      if (String(r.columnId) === concluidas) return { tab: "crm", message: e("errors.crmCompletedOnly") };
      if (r.when === "only_pending" && !item.required) return { tab: "crm", message: e("errors.crmOnlyPending", { n: i + 1 }) };
    }
  }
  for (let i = 0; i < form.workOrderRoutes.length; i += 1) {
    const rota = form.workOrderRoutes[i];
    const n = i + 1;
    // O primeiro dado com as mensagens de antes; os do "E também", com o número do dado.
    const dados = [rota, ...rota.also];
    for (let j = 0; j < dados.length; j += 1) {
      const onde = j === 0 ? { n } : { n, m: j + 1 };
      const sufixo = j === 0 ? "" : "At";
      const valores = routeValues(dados[j].valuesText);
      const naRegra = { tab: "completion", routeUid: rota.uid };
      if (!valores.length) return { ...naRegra, message: e(`errors.routeValues${sufixo}`, onde) };
      if (valores.length > WORK_ORDER_ROUTE_MAX_VALUES) {
        return { ...naRegra, message: e(`errors.routeTooMany${sufixo}`, { ...onde, max: WORK_ORDER_ROUTE_MAX_VALUES }) };
      }
      if (valores.some((v) => v.length > WORK_ORDER_ROUTE_MAX_VALUE_LENGTH)) {
        return { ...naRegra, message: e(`errors.routeLong${sufixo}`, { ...onde, max: WORK_ORDER_ROUTE_MAX_VALUE_LENGTH }) };
      }
    }
    if (new Set(dados.map((d) => d.field)).size !== dados.length) {
      return { tab: "completion", routeUid: rota.uid, message: e("errors.routeRepeated", { n }) };
    }
    if (rota.queueId === "" && rota.userId === "") return { tab: "completion", routeUid: rota.uid, message: e("errors.routeDestination", { n }) };
  }
  if (form.customerExport.enabled) {
    if (!form.workOrders.enabled) return { tab: "completion", message: e("errors.customerExportNeedsWorkOrders") };
    if (form.customerExport.integrationId === "") return { tab: "completion", message: e("errors.customerExportAccount") };
  }
  return null;
};

const DefinitionEditor = ({ definitionId, onBack, onClose, onDirtyChange }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({
    models: [],
    defaultModel: "gpt-5-mini",
    aiUsers: [],
    connections: [],
    workOrderSources: [],
    crmBoards: [],
    queues: [],
    users: [],
    customerExportTargets: [],
  });
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("general");
  // Lista aberta na aba Itens, com a lista de cada garantia.
  const [itemsView, setItemsView] = useState("in");
  const [conexaoRemovida, setConexaoRemovida] = useState(false);
  // Fila ou pessoa do destino apagada depois de escolhida (conclusão / passagem / regras).
  const [destinoRemovido, setDestinoRemovido] = useState({ completion: false, handoff: false, routes: {} });
  // Conta do Contele removida depois de escolhida: sai do formulário e a aba avisa.
  const [exportAccountRemoved, setExportAccountRemoved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data: opts }, def] = await Promise.all([
          api.get("/triage-definitions/options"),
          definitionId ? api.get(`/triage-definitions/${definitionId}`).then((r) => r.data) : Promise.resolve(null),
        ]);
        if (!alive) return;
        setOptions(opts);
        const inicial = def ? fromDefinition(def) : emptyForm(opts.defaultModel);
        // Conexão do contato ativo apagada depois de escolhida: sai do formulário
        // (fica "não salvo") e a aba avisa; senão, nenhum salvamento passaria.
        const removida =
          inicial.outreach.whatsappId !== "" &&
          !(opts.connections || []).some((c) => String(c.id) === String(inicial.outreach.whatsappId));
        setConexaoRemovida(removida);
        // Fila ou pessoa do destino apagada (ou a pessoa saiu da fila) depois de
        // escolhida: sai do formulário (fica "não salvo") e a aba avisa, como a conexão.
        const achar = (lista, id) => (lista || []).find((x) => String(x.id) === String(id));
        // O que ainda serve de uma fila e uma pessoa escolhidas.
        const limpar = (fila, pessoa) => {
          let removido = false;
          if (pessoa !== "" && !achar(opts.users, pessoa)) {
            pessoa = "";
            removido = true;
          }
          const dono = pessoa !== "" ? achar(opts.users, pessoa) : null;
          const filaOk =
            fila === "" || (achar(opts.queues, fila) && (!dono || dono.queueIds.some((q) => String(q) === String(fila))));
          if (!filaOk) {
            fila = "";
            removido = true;
          }
          return { fila, pessoa, removido };
        };
        const destino = { ...inicial };
        const removidos = { completion: false, handoff: false, routes: {} };
        [
          ["completion", "completionQueueId", "completionUserId"],
          ["handoff", "handoffQueueId", "handoffUserId"],
        ].forEach(([parte, fila, pessoa]) => {
          const l = limpar(destino[fila], destino[pessoa]);
          destino[fila] = l.fila;
          destino[pessoa] = l.pessoa;
          removidos[parte] = l.removido;
        });
        destino.workOrderRoutes = destino.workOrderRoutes.map((rota) => {
          const l = limpar(rota.queueId, rota.userId);
          if (!l.removido) return rota;
          removidos.routes[rota.uid] = true;
          return { ...rota, queueId: l.fila, userId: l.pessoa };
        });
        setDestinoRemovido(removidos);
        const accountGone =
          destino.customerExport.enabled &&
          destino.customerExport.integrationId !== "" &&
          !(opts.customerExportTargets || []).some((a) => String(a.id) === String(destino.customerExport.integrationId));
        if (accountGone) destino.customerExport = { ...destino.customerExport, integrationId: "" };
        setExportAccountRemoved(accountGone);
        // O template dos lembretes saía por ela: sai junto.
        setForm(
          removida
            ? {
                ...destino,
                outreach: { whatsappId: "", template: null },
                followUp: { ...destino.followUp, stages: destino.followUp.stages.map((st) => ({ ...st, template: null })) },
              }
            : destino
        );
        setSaved(def ? snapshot(inicial) : null);
        setMeta(def ? metaFrom(def) : null);
        // Sem o início pelas OS, a lista usada é a de "sem garantia": ela abre primeiro.
        setItemsView(inicial.warrantySplit.enabled && !inicial.workOrders.enabled ? inicial.warrantySplit.whenUnknown : "in");
      } catch (err) {
        toastError(err);
        if (alive) onBack();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definitionId]);

  // Nova triagem conta como "não salva" desde o início.
  const dirty = !!form && (saved === null || snapshot(form) !== saved);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    const problema = validar(form, itemsView);
    if (problema) {
      setTab(problema.tab);
      // Abre a lista em que o problema está.
      if (problema.warranty) setItemsView(problema.warranty);
      if (problema.uid) set({ items: form.items.map((it) => (it.uid === problema.uid ? { ...it, open: true } : it)) });
      if (problema.routeUid) {
        set({ workOrderRoutes: form.workOrderRoutes.map((r) => (r.uid === problema.routeUid ? { ...r, open: true } : r)) });
      }
      toast.error(problema.message);
      return null;
    }
    setBusy(true);
    try {
      const payload = toPayload(form);
      const { data } = meta?.id
        ? await api.put(`/triage-definitions/${meta.id}`, payload)
        : await api.post("/triage-definitions", payload);
      setSaved(snapshot(form));
      setMeta(metaFrom(data));
      // Salvo, a chave vale para as versões seguintes: renomear o item não a troca mais.
      setForm((f) => ({
        ...f,
        items: f.items.map((it) => ({
          ...it,
          keyTouched: true,
          extraction: { ...it.extraction, fields: it.extraction.fields.map((fl) => ({ ...fl, keyTouched: true })) },
        })),
      }));
      return data;
    } catch (err) {
      toastError(err);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (await save()) toast.success(i18n.t("triage.manager.saved"));
  };

  const handlePublish = async () => {
    let id = meta?.id;
    if (!id || dirty) {
      const data = await save();
      if (!data) return;
      id = data.id;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/triage-definitions/${id}/publish`);
      setMeta(metaFrom(data));
      toast.success(i18n.t("triage.manager.published", { version: data.currentVersion?.version }));
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const aiUserItems = useMemo(() => {
    const users = options.aiUsers || [];
    return users.map((u) => (
      <MenuItem key={u.id} value={u.id} disabled={!u.usable}>
        {u.usable ? u.name : e("general.aiUserUnusable", { name: u.name })}
      </MenuItem>
    ));
  }, [options.aiUsers]);

  let info;
  let infoWarn = false;
  if (dirty) {
    info = e("unsaved");
    infoWarn = true;
  } else if (!meta?.currentVersion) {
    info = e("neverPublished");
  } else if (meta.hasUnpublishedChanges) {
    info = e("unpublished");
    infoWarn = true;
  } else {
    info = e("upToDate", { version: meta.currentVersion.version });
  }

  const field = (props) => (
    <TextField className={classes.field} variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }} {...props} />
  );
  const selectField = (props) => (
    <ModernSelect className={classes.field} size="small" fullWidth InputLabelProps={{ shrink: true }} {...props} />
  );
  // Com a lista de cada garantia: o texto de cada uma (vazio, vale o de cima).
  const porGarantia = (chave, { maxLength, minRows }) =>
    form?.warrantySplit.enabled && (
      <div className={classes.fieldsBox}>
        <div>
          <div className={classes.label}>{e(`messages.byWarranty.${chave}`)}</div>
          <div className={classes.hint}>{e("messages.byWarranty.hint")}</div>
        </div>
        {WARRANTIES.map((w) => (
          <React.Fragment key={w}>
            {field({
              label: e(`items.warranty.lists.${w}`),
              value: form.warrantySplit.texts[w][chave],
              onChange: (ev) =>
                set({
                  warrantySplit: {
                    ...form.warrantySplit,
                    texts: { ...form.warrantySplit.texts, [w]: { ...form.warrantySplit.texts[w], [chave]: ev.target.value } },
                  },
                }),
              inputProps: { maxLength },
              multiline: true,
              minRows,
            })}
          </React.Fragment>
        ))}
      </div>
    );

  return (
    <>
      <div className={classes.header}>
        <button type="button" className={classes.iconBtn} onClick={onBack} disabled={busy} title={e("back")} aria-label={e("back")}>
          <ChevronLeft />
        </button>
        <div className={`${classes.title} ${classes.titleSmall}`}>
          {form?.name?.trim() || (definitionId ? "…" : e("newTitle"))}
        </div>
        <button type="button" className={classes.iconBtn} onClick={onClose} disabled={busy} aria-label={i18n.t("triage.dialogs.back")}>
          <X />
        </button>
      </div>

      <div className={classes.tabs}>
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            className={`${classes.tab} ${tab === key ? classes.tabActive : ""}`}
            onClick={() => setTab(key)}
          >
            {e(`tabs.${key}`)}
            {key === "items" && form ? ` (${form.items.length})` : ""}
          </button>
        ))}
      </div>

      <div className={classes.body}>
        {loading || !form ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
            <CircularProgress size={24} />
          </div>
        ) : tab === "general" ? (
          <div className={classes.form}>
            {field({
              label: e("general.name"),
              placeholder: e("general.namePlaceholder"),
              value: form.name,
              onChange: (ev) => set({ name: ev.target.value }),
              inputProps: { maxLength: 120 },
              autoFocus: !definitionId,
            })}
            {field({
              label: e("general.description"),
              value: form.description,
              onChange: (ev) => set({ description: ev.target.value }),
              inputProps: { maxLength: 2000 },
              multiline: true,
              minRows: 2,
            })}
            <div className={classes.grid2}>
              {selectField({
                label: e("general.model"),
                helperText: e("general.modelHint"),
                value: form.model,
                onChange: (ev) => set({ model: ev.target.value }),
                children: (options.models || []).map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                )),
              })}
              {field({
                type: "number",
                label: e("general.waitTime"),
                helperText: e("general.waitTimeHint"),
                value: form.waitTimeSeconds,
                onChange: (ev) => set({ waitTimeSeconds: ev.target.value }),
                inputProps: { min: 0, max: 120 },
              })}
            </div>
            {selectField({
              label: e("general.aiUser"),
              helperText: e("general.aiUserHint"),
              value: form.aiUserId,
              onChange: (ev) => set({ aiUserId: ev.target.value }),
              displayEmpty: true,
              children: [
                <MenuItem key="none" value="">
                  <em>{e("general.aiUserNone")}</em>
                </MenuItem>,
                ...aiUserItems,
              ],
            })}
            {(options.aiUsers || []).length === 0 && <div className={classes.warnText}>{e("general.noAiUsers")}</div>}
          </div>
        ) : tab === "items" ? (
          <ItemsEditor
            items={form.items}
            onChange={(items) => set({ items })}
            workOrdersEnabled={form.workOrders.enabled}
            split={form.warrantySplit}
            onSplitChange={(warrantySplit, items) => set(items ? { warrantySplit, items } : { warrantySplit })}
            view={itemsView}
            onViewChange={setItemsView}
          />
        ) : tab === "outreach" ? (
          <OutreachEditor
            value={form.outreach}
            onChange={(outreach) =>
              // Outra conexão: o template dos lembretes era da anterior e sai junto.
              set(
                String(outreach.whatsappId) === String(form.outreach.whatsappId)
                  ? { outreach }
                  : { outreach, followUp: { ...form.followUp, stages: form.followUp.stages.map((st) => ({ ...st, template: null })) } }
              )
            }
            connections={options.connections || []}
            removedConnection={conexaoRemovida}
          />
        ) : tab === "workOrders" ? (
          <WorkOrdersEditor
            value={form.workOrders}
            onChange={(workOrders) => set({ workOrders })}
            sources={options.workOrderSources || []}
            outreachConnection={(options.connections || []).find((c) => String(c.id) === String(form.outreach.whatsappId)) || null}
            hasTemplate={!!form.outreach.template}
            onGoToContact={() => setTab("contact")}
          />
        ) : tab === "contact" ? (
          <ContactEditor
            value={form.contactName}
            onChange={(contactName) => set({ contactName })}
            items={form.items}
            workOrdersEnabled={form.workOrders.enabled}
            onGoToWorkOrders={() => setTab("workOrders")}
            onGoToItems={() => setTab("items")}
          />
        ) : tab === "followUp" ? (
          <FollowUpEditor
            value={form.followUp}
            onChange={(atualizar) => setForm((f) => ({ ...f, followUp: atualizar(f.followUp) }))}
            outreachConnection={(options.connections || []).find((c) => String(c.id) === String(form.outreach.whatsappId)) || null}
            replyMode={form.replyMode}
            expireAfterDays={form.expireAfterDays}
            handoff={{ queueId: form.handoffQueueId, userId: form.handoffUserId }}
            queues={options.queues || []}
            users={options.users || []}
            hasRoutes={form.workOrderRoutes.length > 0}
            crmEnabled={form.crm.enabled}
            onGoToCompletion={() => setTab("completion")}
          />
        ) : tab === "crm" ? (
          <CrmEditor value={form.crm} onChange={(crm) => set({ crm })} boards={options.crmBoards || []} items={form.items} />
        ) : tab === "messages" ? (
          <div className={classes.form}>
            <div>
              <div className={classes.label}>{e("messages.replyMode")}</div>
              <RadioGroup
                className={classes.radioGroup}
                value={form.replyMode}
                onChange={(ev) => set({ replyMode: ev.target.value })}
              >
                {["humanized", "configured"].map((v) => (
                  <FormControlLabel key={v} value={v} control={<Radio size="small" color="primary" />} label={e(`messages.replyModes.${v}`)} />
                ))}
              </RadioGroup>
            </div>
            {field({
              label: e("messages.persona"),
              placeholder: e("messages.personaPlaceholder"),
              value: form.persona,
              onChange: (ev) => set({ persona: ev.target.value }),
              inputProps: { maxLength: 6000 },
              multiline: true,
              minRows: 3,
            })}
            {field({
              label: e("messages.supportText"),
              helperText: e("messages.supportTextHint"),
              value: form.supportText,
              onChange: (ev) => set({ supportText: ev.target.value }),
              inputProps: { maxLength: 6000 },
              multiline: true,
              minRows: 3,
            })}
            {porGarantia("supportText", { maxLength: 6000, minRows: 3 })}
            {field({
              label: e("messages.intro"),
              helperText: e("messages.introHint"),
              value: form.intro,
              onChange: (ev) => set({ intro: ev.target.value }),
              inputProps: { maxLength: 2000 },
              multiline: true,
              minRows: 2,
            })}
            {field({
              label: e("messages.completion"),
              placeholder: e("messages.completionPlaceholder"),
              value: form.completion,
              onChange: (ev) => set({ completion: ev.target.value }),
              inputProps: { maxLength: 2000 },
              multiline: true,
              minRows: 2,
            })}
            {porGarantia("completion", { maxLength: 2000, minRows: 2 })}
          </div>
        ) : (
          <div className={classes.form}>
            <div>
              <SectionTitle title={e("completion.ticketAction")} info={e("completion.ticketActionInfo")} />
              <RadioGroup row className={classes.radioGroup} value={form.ticketAction} onChange={(ev) => set({ ticketAction: ev.target.value })}>
                {["close", "keep_pending"].map((v) => (
                  <FormControlLabel key={v} value={v} control={<Radio size="small" color="primary" />} label={e(`completion.actions.${v}`)} />
                ))}
              </RadioGroup>
              {form.ticketAction === "keep_pending" && (
                <div style={{ marginTop: 8 }}>
                  <DestinationFields
                    queueId={form.completionQueueId}
                    userId={form.completionUserId}
                    onChange={({ queueId, userId }) => {
                      set({ completionQueueId: queueId, completionUserId: userId });
                      setDestinoRemovido((r) => ({ ...r, completion: false }));
                    }}
                    queues={options.queues || []}
                    users={options.users || []}
                    removed={destinoRemovido.completion}
                  />
                </div>
              )}
            </div>
            <div>
              <SectionTitle title={e("completion.handoff")} info={e("completion.handoffInfo")} />
              <DestinationFields
                queueId={form.handoffQueueId}
                userId={form.handoffUserId}
                onChange={({ queueId, userId }) => {
                  set({ handoffQueueId: queueId, handoffUserId: userId });
                  setDestinoRemovido((r) => ({ ...r, handoff: false }));
                }}
                queues={options.queues || []}
                users={options.users || []}
                removed={destinoRemovido.handoff}
              />
            </div>
            {/* Exceções pela OS: só com o início pelas OS (ou com regra já feita). */}
            {(form.workOrders.enabled || form.workOrderRoutes.length > 0) && (
              <WorkOrderRoutesEditor
                value={form.workOrderRoutes}
                onChange={(workOrderRoutes) => set({ workOrderRoutes })}
                queues={options.queues || []}
                users={options.users || []}
                removed={destinoRemovido.routes}
                onFixRemoved={(uid) => setDestinoRemovido((r) => ({ ...r, routes: { ...r.routes, [uid]: false } }))}
                workOrdersEnabled={form.workOrders.enabled}
              />
            )}
            {/* Contele: só com conta cadastrada e o início pelas OS (ou com a opção já ligada). */}
            {(((options.customerExportTargets || []).length > 0 && form.workOrders.enabled) || form.customerExport.enabled) && (
              <CustomerExportEditor
                value={form.customerExport}
                onChange={(customerExport) => {
                  set({ customerExport });
                  setExportAccountRemoved(false);
                }}
                accounts={options.customerExportTargets || []}
                items={form.items}
                workOrdersEnabled={form.workOrders.enabled}
                removed={exportAccountRemoved}
              />
            )}
            <div>
              <SectionTitle title={e("completion.expireTitle")} info={e("completion.expireInfo")} />
              <div className={classes.sentenceRow}>
                <span>{e("completion.expireBefore")}</span>
                <TextField
                  className={`${classes.field} ${classes.daysField}`}
                  variant="outlined"
                  size="small"
                  type="number"
                  value={form.expireAfterDays}
                  onChange={(ev) => set({ expireAfterDays: ev.target.value })}
                  inputProps={{ min: 0, max: 365, "data-field": "expireAfterDays", "aria-label": e("completion.expireTitle") }}
                />
                <span>{e("completion.expireUnit")}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={classes.footer}>
        <div className={`${classes.footerInfo} ${infoWarn ? classes.footerWarn : ""}`}>{info}</div>
        <div className={classes.footerButtons}>
          {busy && <CircularProgress size={18} />}
          <button type="button" className={classes.btnSecondary} onClick={handleSave} disabled={busy || loading || !form || !dirty}>
            {e("save")}
          </button>
          <button
            type="button"
            className={classes.btnPrimary}
            onClick={handlePublish}
            disabled={busy || loading || !form || (!dirty && meta?.currentVersion && !meta.hasUnpublishedChanges)}
          >
            {e("publish")}
          </button>
        </div>
      </div>
    </>
  );
};

export default DefinitionEditor;
